import { PermissionItem } from "../components/PermissionList";

export enum PermissionSection {
  dashboard = 'Dashboard',
  training = 'Training',
  phishing = 'Phishing',
  reports = 'Reports',
  settings = 'Settings',
  systemsManagement = 'Systems Management',
  forceSync = 'Force Sync Management',
  manageUsers = 'Manage Users LMS',
  featureManager = 'Feature Manager',
}

export enum PermissionGroup {
  auditSecurity = 'Audit & Security',
  users = 'Users',
  groups = 'Groups',
  tenant = 'Tenant',
  managers = 'Managers',
  owners = 'Owners',
  admins = 'Admins',
  superAdmins = 'Super Admins',
  ownershipGroups = 'Ownership Groups',
  tenantRoles = 'Tenant Roles',
  mapping = 'Mapping',
  newPermissions = 'New Permissions',
}

export interface PermissionSectionData {
  key: string;
  label: string;
  code?: string;
  groups: any[];
}

export interface PermissionTemplateItem {
  section: string;
  group?: string;
  code: string;
}

export const PERMISSION_TEMPLATE: PermissionTemplateItem[] = [

  // ==========================================================
  // Dashboard
  // ==========================================================

  { section: PermissionSection.dashboard, code: 'view_dashboard_elearning' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_my_results' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_overview' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_phishing' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_saq_compliance' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_scanning' },
  { section: PermissionSection.dashboard, code: 'view_user_activity' },
  { section: PermissionSection.dashboard, code: 'generate_dashboard_executive_summary' },
  { section: PermissionSection.dashboard, code: 'view_dashboard_spotcheck' },


  // ==========================================================
  // Training
  // ==========================================================

  {
    section: PermissionSection.training,
    code: 'view_training_assignment_wizard',
  },


  // ==========================================================
  // Phishing
  // ==========================================================

  {
    section: PermissionSection.phishing,
    code: 'view_phishing_manager',
  },
  {
    section: PermissionSection.phishing,
    code: 'view_phishing_template_manager',
  },

  // ==========================================================
  // Reports
  // ==========================================================

  {
    section: PermissionSection.reports,
    code: 'view_standard_reports',
  },
  {
    section: PermissionSection.reports,
    code: 'create_report_current_historical',
  },
  {
    section: PermissionSection.reports,
    group: PermissionGroup.auditSecurity,
    code: 'view_user_audit_log',
  },
  {
    section: PermissionSection.reports,
    group: PermissionGroup.auditSecurity,
    code: 'export_user_audit_log',
  },


  // ==========================================================
  // Settings - Users
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'view_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'add_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'edit_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'activate_disable_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'delete_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'restore_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'move_user__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'reset_2fa__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'reset_password__user',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'add_user_notification',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.users,
    code: 'export_users',
  },


  // ==========================================================
  // Settings - Groups
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'group_manager_view_page',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'add_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'edit_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'move_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'change_group_password',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'delete_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'restore_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.groups,
    code: 'export_groups',
  },


  // ==========================================================
  // Settings - Tenant
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenant,
    code: 'add_tenant',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenant,
    code: 'edit_tenant',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenant,
    code: 'delete_tenant',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenant,
    code: 'restore_tenant',
  },


  // ==========================================================
  // Settings - Managers
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'view_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'add_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'edit_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'activate_disable_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'delete_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'restore_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'move_user__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'reset_2fa__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'reset_password__manager',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.managers,
    code: 'user_manager_screen_view',
  },


  // ==========================================================
  // Settings - Owners
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'view_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'add_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'edit_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'activate_disable_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'delete_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'restore_user__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'reset_2fa__owner',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.owners,
    code: 'reset_password__owner',
  },


  // ==========================================================
  // Settings - Admins
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'view_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'add_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'edit_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'activate_disable_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'delete_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'restore_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'move_user__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'reset_2fa__admin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.admins,
    code: 'reset_password__admin',
  },


  // ==========================================================
  // Settings - Super Admins
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'view_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'add_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'edit_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'activate_disable_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'delete_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'restore_user__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'reset_2fa__superadmin',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.superAdmins,
    code: 'reset_password__superadmin',
  },


  // ==========================================================
  // Settings - Ownership Groups
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.ownershipGroups,
    code: 'view_ownership_groups',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.ownershipGroups,
    code: 'add_ownership_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.ownershipGroups,
    code: 'edit_ownership_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.ownershipGroups,
    code: 'delete_ownership_group',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.ownershipGroups,
    code: 'export_ownership_groups',
  },


  // ==========================================================
  // Settings - Tenant Roles
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'add_system_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'clone_system_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'delete_system_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'edit_system_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'export_system_roles',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'system_role_manager_view_page',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'restore_primary_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'create_secondary_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'edit_secondary_role',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.tenantRoles,
    code: 'delete_secondary_role',
  },


  // ==========================================================
  // Settings - Mapping
  // ==========================================================

  {
    section: PermissionSection.settings,
    group: PermissionGroup.mapping,
    code: 'view_mapping_wizard',
  },
  {
    section: PermissionSection.settings,
    group: PermissionGroup.mapping,
    code: 'edit_mapping_wizard',
  },


  // ==========================================================
  // Systems Management
  // ==========================================================

  {
    section: PermissionSection.featureManager,
    code: 'view_feature_manager',
  },
  {
    section: PermissionSection.featureManager,
    code: 'manage_feature_manager',
  },
  {
    section: PermissionSection.systemsManagement,
    code: 'get_systems',
  },
  {
    section: PermissionSection.systemsManagement,
    code: 'get_system_by_id',
  },

  // ==========================================================
  // Manage Users
  // ==========================================================

  {
    section: PermissionSection.manageUsers,
    code: 'lms',
  },

   // ==========================================================
  // Force Sync
  // ==========================================================

  {
    section: PermissionSection.forceSync,
    code: 'create_force_sync',
  },
];

const hiddenPermissions = ['assign_secondary_role'];


// ============================================================
// VIEW PERMISSION DEPENDENCY
// ============================================================

export const getViewPermissionCode = (
  code: string,
): string | null => {
  switch (true) {

    case code === 'export_user_audit_log':
      return 'view_user_audit_log';

    case code === 'manage_feature_manager':
      return 'view_feature_manager';

    case code === 'get_system_by_id':
      return 'get_systems';

    case code === 'edit_mapping_wizard':
      return 'view_mapping_wizard';

    case code === 'create_report_current_historical':
      return 'view_standard_reports';

    case code.includes('ownership'):
      return 'view_ownership_groups';

    case code.includes('__owner'):
      return 'view_user__owner';

    case code.includes('__admin'):
      return 'view_user__admin';

    case code.includes('__superadmin'):
      return 'view_user__superadmin';

    case code.includes('__manager') || code.includes('user_manager'):
      return 'view_user__manager';

    case code.includes('__user') || code.includes('user_notification')|| code.includes('user_notification') || code.includes('export_users'):
      return 'view_user__user';

    case code.includes('group'):
      return 'group_manager_view_page';

    case code.includes('_ownership_group'):
      return 'view_ownership_groups';

    case code.includes('_role'):
      return 'system_role_manager_view_page';

    default:
      return null;
  }
};

export const FORCED_DEPENDENCIES: Record<string, string[]> = {
  view_user__user: ['user_manager_screen_view'],
};

// ============================================================
// CHECK WHETHER VIEW PERMISSION MUST BE LOCKED
// ============================================================

export const isViewLocked = (viewCode: string, items: PermissionItem[], checkedMap: Record<string, boolean>) => {
  return items.some(item => {
    if (getViewPermissionCode(item.code) === item.code) return false;

    return (
      getViewPermissionCode(item.code) === viewCode &&
      checkedMap[item.key]
    );
  });
};

const ADMIN_SPECIFIC_CODES = new Set([
  'view_phishing_manager',
  'view_phishing_template_manager'
]);

export const buildPermissionList = (
  apiPermissionList: PermissionSectionData[],
  adminSpecific = true,
): PermissionSectionData[] => {

  const apiPermissions = apiPermissionList.flatMap(section =>
    section.groups.flatMap(group => group.items),
  );

  const apiPermissionMap = new Map(
    apiPermissions.map(permission => [
      permission.code,
      permission,
    ]),
  );

  const sections: PermissionSectionData[] = [];

  PERMISSION_TEMPLATE.forEach(templatePermission => {
    if (adminSpecific && ADMIN_SPECIFIC_CODES.has(templatePermission.code)) {
      return;
  }
    const apiPermission = apiPermissionMap.get(
      templatePermission.code,
    );

    if (!apiPermission) {
      return;
    }

    let section = sections.find(
      item => item.key === templatePermission.section,
    );

    if (!section) {
      section = {
        key: templatePermission.section,
        label: templatePermission.section,
        groups: [],
      };

      sections.push(section);
    }

    let group = section.groups.find(
      item => item.title === templatePermission.group,
    );

    if (!group) {
      group = {
        title: templatePermission.group,
        items: [],
      };
      section.groups.push(group);
    }
    group.items.push(apiPermission);
  });

  const templateCodes = new Set(
    PERMISSION_TEMPLATE.map(
      permission => permission.code,
    ),
  );

  const newPermissions = apiPermissions.filter(
    permission =>
      !templateCodes.has(permission.code) &&
      !hiddenPermissions.includes(permission.code),
  );

  if (newPermissions.length > 0) {

    let settingsSection = sections.find(
      section =>
        section.key === PermissionSection.settings,
    );

    if (!settingsSection) {
      settingsSection = {
        key: PermissionSection.settings,
        label: PermissionSection.settings,
        groups: [],
      };

      sections.push(settingsSection);
    }

    settingsSection.groups.push({
      title: 'New Permissions',
      items: newPermissions,
    });
  }

  return sections;
};

export const getPermissionTranslationKey = (
  code?: string,
): string => {
  if (!code) return '';

  return `permissions.${code}`;
};

export const getPermissionGroupTranslationKey = (group: string) => {
  const key = Object.keys(PermissionGroup).find(
    key =>
      PermissionGroup[key as keyof typeof PermissionGroup] === group,
  );

  return `sidebar.settings.permissionList.groups.${key ?? group}`;
};
export const getPermissionSectionTranslationKey = (group: string) => {
  const key = Object.keys(PermissionSection).find(
    key =>
      PermissionSection[key as keyof typeof PermissionSection] === group,
  );

  return `sidebar.settings.permissionList.sections.${key ?? group}`;
};